/* ORIO MobileNav — bottom tab bar with central SOS (Apple Fitness style)
   --------------------------------------------------------------------
   5 destinations + 1 SOS (center, prominent):
     [ Home · Mappa · SOS · Missioni · Profilo ]
   Lives ABOVE the Emergent platform badge by reserving the rightmost
   slot's right padding when needed, and adding env(safe-area-inset-bottom).
   
   Functional behavior preserved: the SOS center button triggers the
   emergency flow (geolocation → POST /emergencies) exactly like before.
*/
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home as HomeIcon, Map, AlertTriangle, HeartHandshake, Heart, UserCircle2, X, Lock, Crown, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { hapticEmergency, hapticTap } from "@/lib/haptics";

const TABS = [
  { to: "/app",          icon: HomeIcon,       label: "Home",     match: (p) => p === "/app" },
  { to: "/app/mappa",    icon: Map,            label: "Mappa",    match: (p) => p.startsWith("/app/mappa") },
  // center SOS placeholder — handled separately
  { to: "/app/match",    icon: Heart,          label: "Match",    match: (p) => p.startsWith("/app/match"), pro: true },
  { to: "/app/missioni", icon: HeartHandshake, label: "Missioni", match: (p) => p.startsWith("/app/missioni") },
];

export default function MobileNav() {
  const { user } = useAuth();
  const { isPro, canStartTrial, startTrial } = useSubscription();
  const navigate = useNavigate();
  const loc = useLocation();
  const [emOpen, setEmOpen] = useState(false);
  const [proOpen, setProOpen] = useState(false);
  const [dogs, setDogs] = useState([]);
  const [dogId, setDogId] = useState("");
  const [hasActiveSos, setHasActiveSos] = useState(false);

  // Poll active SOS emergencies — SOS button pulses ONLY when there's one
  // nearby (within user's SOS notification radius) OR one launched by me.
  // This keeps the icon static in normal state (per user feedback).
  React.useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await api.get("/emergencies/active");
        if (cancelled) return;
        const mine = (data || []).some((e) => e.owner_id === user?.id);
        // "Nearby" heuristic: any active emergency (server already limits
        // exposure by radius via the map filter; here we just flag existence).
        const anyActive = (data || []).length > 0;
        setHasActiveSos(mine || anyActive);
      } catch { /* silent */ }
    };
    check();
    const t = setInterval(check, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user?.id]);

  const openEmergency = async () => {
    hapticEmergency();
    // Pro gate — Free users see the SOS paywall BEFORE the confirm sheet.
    if (!isPro) {
      setProOpen(true);
      return;
    }
    try {
      const { data } = await api.get("/dogs");
      const real = data.filter((d) => !d.is_stub);
      if (real.length === 0) {
        toast.error("Crea prima un passaporto cane");
        navigate("/app/cani/nuovo");
        return;
      }
      setDogs(real);
      setDogId(real[0].id);
      setEmOpen(true);
    } catch { toast.error("Errore"); }
  };

  const launch = () => {
    hapticEmergency();
    if (!dogId || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async (p) => {
        try {
          const { data } = await api.post("/emergencies", {
            dog_id: dogId, lat: p.coords.latitude, lng: p.coords.longitude,
            last_seen_note: "Emergenza attivata dal menu",
          });
          toast.success(`Allerta diffusa · ${data.notified || 0} utenti notificati`);
          setEmOpen(false);
          navigate("/app/mappa");
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
      },
      () => toast.error("Impossibile ottenere posizione")
    );
  };

  return (
    <>
      {/* Hide the whole bottom tab bar on immersive pages — walk (dark focus)
          and billing (checkout flow should be free of distractions). */}
      {(loc.pathname.startsWith("/app/passeggiate") ||
        loc.pathname.startsWith("/billing")) ? null : (
      <>
      {/* Bottom Tab Bar — mobile only. The right edge stays clear of the
          Emergent badge (which sits bottom-right at z-9999); the bar runs
          full width and uses backdrop blur so the badge floats above it. */}
      <nav
        data-testid="bottom-nav"
        aria-label="Navigazione principale"
        className="md:hidden fixed inset-x-0 z-[400]"
        style={{
          // Lift above the Emergent platform badge (40px tall, 16px from bottom).
          // Total clearance ≈ 56px so the rightmost tab is never covered by the
          // platform badge that sits in the bottom-right corner.
          bottom: "max(calc(env(safe-area-inset-bottom, 0px) + 56px), 56px)",
        }}
      >
        <div
          className="mx-2 mb-2 rounded-[28px] border border-[#F0EBE3] bg-white/95 shadow-[0_-8px_30px_-8px_rgba(0,0,0,0.12)]"
          style={{ backdropFilter: "blur(18px) saturate(160%)", WebkitBackdropFilter: "blur(18px) saturate(160%)" }}
        >
          <div className="grid grid-cols-5 items-center px-2 pt-2 pb-1">
            <TabButton tab={TABS[0]} active={TABS[0].match(loc.pathname)} />
            <TabButton tab={TABS[1]} active={TABS[1].match(loc.pathname)} />

            {/* Center SOS — elevated round button */}
            <div className="flex items-start justify-center -mt-7">
              <motion.button
                data-testid="bottom-nav-sos"
                onClick={openEmergency}
                aria-label="Emergenza SOS"
                whileTap={{ scale: 0.92 }}
                whileHover={{ scale: 1.04 }}
                transition={{ type: "spring", stiffness: 380, damping: 22 }}
                className="relative h-[60px] w-[60px] rounded-full flex items-center justify-center"
                style={{
                  background: "linear-gradient(135deg, #FF5C2A 0%, #E94545 100%)",
                  boxShadow: "0 8px 24px -4px rgba(233,69,69,0.55), 0 0 0 4px #FFFFFF",
                }}
              >
                <span className={`absolute inset-0 rounded-full pointer-events-none ${hasActiveSos ? "orio-pulse" : ""}`} />
                <AlertTriangle className="h-6 w-6 text-white" strokeWidth={2.5} />
              </motion.button>
            </div>

            <TabButton tab={TABS[2]} active={TABS[2].match(loc.pathname)} showLock={!isPro} />
            <TabButton tab={TABS[3]} active={TABS[3].match(loc.pathname)} />
          </div>
        </div>
      </nav>

      {/* Spacer so page content doesn't sit under the nav. The fixed nav lives
          at bottom = max(safe-area + 56px, 56px) and is ~88px tall, so we need
          at least 56 + 88 + safe-area = ~178px clearance. Padding callers
          (Home etc.) can still add their own pb-* but this guarantees the
          tail of every page is visible above the floating tab bar. */}
      <div
        className="md:hidden"
        aria-hidden
        style={{ height: "max(calc(env(safe-area-inset-bottom, 0px) + 168px), 168px)" }}
      />

      {/* Pro paywall for Free users trying SOS — dark Apple Fitness style */}
      <AnimatePresence>
        {proOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="md:hidden fixed inset-0 z-[1700] flex items-end justify-center"
            data-testid="sos-pro-paywall"
            onClick={() => setProOpen(false)}
          >
            <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(14px)" }} />
            <motion.div
              initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 80 }}
              transition={{ type: "spring", damping: 26, stiffness: 280 }}
              className="relative w-full max-w-md rounded-t-[28px] p-6 overflow-hidden border text-white"
              style={{
                background:
                  "radial-gradient(120% 60% at 0% 0%, rgba(233,69,69,0.30) 0%, rgba(233,69,69,0) 60%), " +
                  "linear-gradient(180deg, #14100C 0%, #0B0806 55%, #050403 100%)",
                borderColor: "rgba(255,255,255,0.12)",
                paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
                boxShadow: "0 40px 80px -20px rgba(0,0,0,0.6), 0 24px 48px -12px rgba(233,69,69,0.35)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mx-auto h-1 w-10 rounded-full bg-white/20 mb-4" />
              <div className="relative flex items-start gap-3">
                <div className="h-11 w-11 rounded-2xl flex items-center justify-center shrink-0 text-white"
                  style={{ background: "linear-gradient(135deg, #E94545 0%, #FF5C2A 100%)",
                           boxShadow: "0 10px 22px -8px rgba(233,69,69,0.55)" }}>
                  <Crown className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-[0.20em] font-bold inline-flex items-center gap-1.5"
                       style={{ color: "#F4B83C" }}>
                    <Lock className="h-3 w-3" /> Funzione ORIO Pro
                  </div>
                  <h3 className="font-display text-2xl leading-tight text-white mt-0.5">
                    Il pulsante SOS è riservato agli abbonati.
                  </h3>
                  <p className="text-[13px] text-white/65 mt-1.5 leading-snug">
                    Con ORIO Pro allerti fino a 80 km di raggio e ricevi soccorritori in tempo reale.
                    Inizia 30 giorni gratis, disdici quando vuoi.
                  </p>
                </div>
                <button
                  onClick={() => setProOpen(false)}
                  aria-label="Chiudi"
                  data-testid="sos-pro-close"
                  className="h-9 w-9 rounded-full flex items-center justify-center shrink-0 border -mt-1 -mr-1"
                  style={{ background: "rgba(255,255,255,0.06)", borderColor: "rgba(255,255,255,0.12)" }}
                >
                  <X className="h-4 w-4 text-white/85" />
                </button>
              </div>
              <motion.button
                whileTap={{ scale: 0.97 }}
                data-testid="sos-pro-cta"
                onClick={() => {
                  setProOpen(false);
                  if (canStartTrial) startTrial();
                  else navigate("/billing");
                }}
                className="mt-5 w-full py-3.5 rounded-full text-white font-semibold text-[14px] inline-flex items-center justify-center gap-2"
                style={{
                  background: "linear-gradient(135deg, #FF7A1A 0%, #E94545 100%)",
                  boxShadow: "0 22px 40px -14px rgba(233,69,69,0.75), 0 0 0 6px rgba(255,255,255,0.04)",
                }}
              >
                <Sparkles className="h-4 w-4" />
                {canStartTrial ? "Inizia 30 giorni gratis" : "Vedi i piani"}
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* SOS confirm sheet */}
      <AnimatePresence>
        {emOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="md:hidden fixed inset-0 z-[1600] flex items-end justify-center"
            data-testid="sos-confirm"
            onClick={() => setEmOpen(false)}
          >
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
            <motion.div
              initial={{ y: 80 }}
              animate={{ y: 0 }}
              exit={{ y: 80 }}
              transition={{ type: "spring", damping: 26, stiffness: 280 }}
              className="relative w-full max-w-md rounded-t-[28px] border border-[#F0EBE3] bg-white p-6"
              style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mx-auto h-1 w-10 rounded-full bg-[#E8E2D6] mb-5" />
              <div className="flex items-start gap-3 mb-2">
                <div className="h-11 w-11 rounded-2xl bg-[#FDECEC] text-[#E94545] flex items-center justify-center shrink-0">
                  <AlertTriangle className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-display text-xl text-[#E94545] leading-tight">Dichiarare emergenza?</h3>
                  <p className="text-sm text-[#6B6B6B] mt-1 leading-snug">
                    Pubblicherai il passaporto sulla mappa e notificherai gli utenti ORIO fino a 80 km.
                  </p>
                </div>
                <button
                  onClick={() => setEmOpen(false)}
                  aria-label="Chiudi"
                  className="h-9 w-9 rounded-full bg-[#F4EFE6] flex items-center justify-center shrink-0 hover:bg-[#EAE3D2] -mt-1 -mr-1"
                >
                  <X className="h-4 w-4 text-[#6B6B6B]" />
                </button>
              </div>

              {dogs.length > 1 && (
                <select
                  value={dogId}
                  onChange={(e) => setDogId(e.target.value)}
                  className="w-full mt-4 px-4 py-3 rounded-2xl bg-[#FBF7F1] border border-[#F0EBE3] outline-none text-[#1A1A1A]"
                >
                  {dogs.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              )}
              {dogs.length === 1 && (
                <div className="mt-4 px-4 py-3 rounded-2xl bg-[#FBF7F1] border border-[#F0EBE3] text-sm text-[#1A1A1A]">
                  Cane: <span className="font-semibold">{dogs[0].name}</span>
                </div>
              )}

              <div className="flex gap-2 mt-5">
                <button
                  onClick={() => setEmOpen(false)}
                  className="flex-1 px-5 py-3.5 rounded-full bg-[#F4EFE6] text-[#1A1A1A] text-sm font-medium hover:bg-[#EAE3D2]"
                >
                  Annulla
                </button>
                <button
                  onClick={launch}
                  data-testid="sos-confirm-go"
                  className="flex-1 px-5 py-3.5 rounded-full text-white text-sm font-semibold orio-pulse"
                  style={{ background: "linear-gradient(135deg, #FF5C2A 0%, #E94545 100%)" }}
                >
                  Diffondi allerta
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </>
      )}
    </>
  );
}

function TabButton({ tab, active, showLock = false }) {
  const { icon: Icon, label, to } = tab;
  return (
    <Link
      to={to}
      data-testid={`bottom-nav-${label.toLowerCase()}`}
      className="flex flex-col items-center justify-center gap-0.5 py-1.5 px-1 rounded-2xl"
    >
      <motion.div
        animate={{ scale: active ? 1.05 : 1 }}
        transition={{ type: "spring", stiffness: 380, damping: 22 }}
        className="flex items-center justify-center h-7 relative"
      >
        <Icon
          className="h-[22px] w-[22px]"
          strokeWidth={active ? 2.3 : 1.8}
          style={{ color: active ? "#FF7A1A" : "#9A9A9A" }}
        />
        {showLock && (
          <span
            aria-hidden
            className="absolute -top-1 -right-1.5 h-3.5 w-3.5 rounded-full flex items-center justify-center border border-white"
            style={{ background: "linear-gradient(135deg,#FFB020,#F4B83C)" }}
          >
            <Lock className="h-2 w-2 text-[#0F0F0F]" strokeWidth={3} />
          </span>
        )}
      </motion.div>
      <span
        className="text-[10.5px] font-medium leading-none"
        style={{ color: active ? "#FF7A1A" : "#9A9A9A" }}
      >
        {label}
      </span>
    </Link>
  );
}
